import { JSX, Match, Show, Switch } from "solid-js";
import { ArrowRight } from "~/assets/svg_icons/svg_icons";
import HubbleImage from "~/widgets/hubble_image";

const COIN_ICON_URL = "https://assets.myhubble.money/web-assets/coinIcon.svg";

type BrandCardWebstoreTelescopeProps = {
  brandKey: string;
  name: string;
  logoUrl: string;
  iconImageUrl: string;
  categoryName: string | null;
  discount?: number;
  subtitle?: string;
  onClick?: () => void;
  forceDarkTheme?: boolean;
  cardBackgroundColor: string;
  showHubbleCoinReward?: boolean;
  categoryNameToTitleMap?: Record<string, string>;
  hideCategoryName?: boolean;
};

/**
 * Brand Card Component for Webstore Telescope
 * Shared component for home and category pages
 */
export function BrandCardWebstoreTelescope(
  props: BrandCardWebstoreTelescopeProps
): JSX.Element {
  const getCategoryTitle = () => {
    if (!props.categoryName) return "";
    return props.categoryNameToTitleMap?.[props.categoryName] ?? "";
  };

  return (
    <div
      class="group flex w-full cursor-pointer flex-col gap-2"
      classList={{
        "telescope-dark-theme": props.forceDarkTheme,
      }}
      onClick={props.onClick}
    >
      {/* Card Container */}
      <div class="relative aspect-[312/348] w-full overflow-hidden rounded-[16px]">
        <HubbleImage
          src={props.iconImageUrl}
          alt={props.name}
          class="absolute inset-0 size-full object-cover"
          errorImage={props.logoUrl}
        />
      </div>

      {/* Details Section */}
      <div class="relative box-border flex w-full flex-col items-start px-1 py-0">
        {/* Name and Subtitle */}
        <div class="flex w-full flex-col items-start gap-[2px]">
          <p class="w-full overflow-hidden text-ellipsis whitespace-nowrap text-para-3-semi-bold text-text-normal-primary">
            {props.name}
          </p>
          <Show when={props.subtitle}>
            <p class="w-full overflow-hidden text-ellipsis whitespace-nowrap text-[12px] font-semibold leading-[16px] tracking-[-0.12px] text-text-normal-primary">
              {props.subtitle}
            </p>
          </Show>
          <Show when={props.categoryName && !props.hideCategoryName}>
            <p class="w-full overflow-hidden text-ellipsis whitespace-nowrap text-label-regular text-text-normal-secondary">
              {getCategoryTitle()}
            </p>
          </Show>
        </div>

        {/* Discount Badge */}
        <Show when={props.discount}>
          <Switch>
            {/* Show coin icon for POSTPAID rewards */}
            <Match when={props.showHubbleCoinReward}>
              <div class="flex items-center gap-1">
                <img class="size-5" src={COIN_ICON_URL} alt="coin" />
                <p class="text-title-4-semi-bold" style={{ color: "#d98f00" }}>
                  {props.discount}
                  <span class="text-para-3-bold uppercase">%</span>
                </p>
              </div>
            </Match>

            {/* Default: Show green discount */}
            <Match when={true}>
              <div class="flex flex-row items-end  ">
                <p class="text-title-4-semi-bold text-success-base">
                  {props.discount}
                </p>
                <p class="pb-[2.4px] text-para-3-bold text-success-base">
                  % OFF
                </p>
              </div>
            </Match>
          </Switch>
        </Show>

        {/* Hover Arrow */}
        <ArrowRight
          class="absolute bottom-2 right-2 hidden text-text-normal-primary opacity-0 transition-opacity group-hover:opacity-100 sm:block"
          width={20}
          height={20}
        />
      </div>
    </div>
  );
}
